import { type Node, NodeFilterSortFields, NodeStatusEnum } from "@/swagger-models/cluster-service-client";
import { ECustomCell, type IStatusColOptions, type ITableColumn } from "@/models/table.model";
import { memoryFormat } from "@/utils/format.util";
import { tableFormatUtil } from "@/utils/table-format.util/table-format.util";
import { CLUSTER_COLUMN_FILTER_NAME, EColumnFilterType } from "@/models/filter.model";
import { nodeUtil } from "@/utils/node.util";
import { filterUtil } from "@/utils/filter.util/filter.util";

const _formatMemoryValue = (val: number | undefined): string =>
  memoryFormat(tableFormatUtil.getFormattedNumberOrDefault(val));

const formatReadyTotalGpu = (readyGpus?: number, totalGpus?: number): string => {
  if (readyGpus === undefined || totalGpus === undefined) {
    return "-";
  }
  return `${readyGpus}/${totalGpus}`;
};

export interface IAdditionalNodeTableFields {
  totalGpus?: number;
  readyGpus?: number;
  freeGpus?: number;
  totalGpuMemoryBytes?: number;
  allocatedGpus?: number;
  allocatedGpuMemory?: number;
  totalCpuCores?: number;
  totalCpuMemoryBytes?: number;
  allocatedCpuCores?: number;
  allocatedCpuMemoryBytes?: number;
}

export interface INodeTable extends Node {
  additionalFields?: IAdditionalNodeTableFields;
}

export const nodeColumnName = {
  name: NodeFilterSortFields.Name,
  status: NodeFilterSortFields.Status,
  nodePool: NodeFilterSortFields.NodePool,
  cluster: CLUSTER_COLUMN_FILTER_NAME,
  nvLinkDomainUid: NodeFilterSortFields.NvLinkDomainUid,
  nvLinkCliqueId: NodeFilterSortFields.NvLinkCliqueId,
  gpuType: "gpuType",
  gpuCount: "gpuCount",
  freeGpu: "freeGpu",
  totalGpuMemoryBytes: "totalGpuMemoryBytes",
  allocatedGpus: "allocatedGpus",
  totalCpuCores: "totalCpuCores",
  totalCpuMemoryBytes: "TotalCpuMemoryBytes",
  allocatedCpuCores: "allocatedCpuCores",
  allocatedCpuMemoryBytes: "allocatedCpuMemoryBytes",
  allocatedGpuMemory: "allocatedGpuMemory",
  pods: "pods",
  readyTotalGPU: "readyTotalGPU",
  readyTotalGpuMemory: "readyTotalGpuMemory", // Barak:todo - missing telemetry for ready
  readyTotalCpuCores: "readyTotalCpuCores", // Barak:todo - missing telemetry for ready
  readyTotalCpuMemory: "readyTotalCpuMemory", // Barak:todo - missing telemetry for ready
};

enum ENodeColumnLabel {
  Name = "Node",
  GpuType = "GPU type",
  GpuCount = "Total GPU devices",
  TotalGpuMemoryBytes = "Total GPU memory",
  AllocatedGpus = "Allocated GPUs",
  TotalCpuCores = "Total CPU (cores)",
  TotalCpuMemoryBytes = "Total CPU memory",
  AllocatedCpuCores = "Allocated CPU (cores)",
  AllocatedCpuMemoryBytes = "Allocated CPU memory",
  AllocatedGpuMemory = "Allocated GPU memory",
  Status = "Status",
  NodePool = "Node Pool",
  Cluster = "Cluster",
  NvLinkDomainUid = "MNNVL Domain UID",
  NvLinkCliqueId = "MNNVL domain clique ID",
  ReadyTotalGPU = "Ready / total GPU devices",
  FreeGpu = "Free GPU devices",
  AllocatedGpu = "Allocated GPU",
  Pods = "Pod(s)",
}

const nodeV2IndexColumns: Record<string, ITableColumn> = {
  /*** clusterServiceApi.getNodes columns ***/
  name: {
    name: nodeColumnName.name,
    label: ENodeColumnLabel.Name,
    field: (node: INodeTable): string => node.name,
    sortable: true,
    align: "left",
    filter: {
      type: EColumnFilterType.FreeText,
    },
  },
  nodePool: {
    name: nodeColumnName.nodePool,
    label: ENodeColumnLabel.NodePool,
    field: (node: INodeTable): string => node.nodePool || "-",
    sortable: true,
    align: "left",
    filter: {
      type: EColumnFilterType.FreeText,
    },
  },
  status: {
    name: nodeColumnName.status,
    label: ENodeColumnLabel.Status,
    field: (node: INodeTable): Node => node,
    sortable: true,
    align: "left",
    exportFormat: (node: INodeTable) => node.status || "-",
    customCell: ECustomCell.STATUS_COL,
    format: (node: INodeTable): IStatusColOptions => {
      return nodeUtil.getStatusColOptions(node);
    },
    filter: {
      type: EColumnFilterType.EnumBased,
      selectOptions: filterUtil.parseEnumToSelectOptions(NodeStatusEnum),
    },
  },
  gpuType: {
    name: nodeColumnName.gpuType,
    label: ENodeColumnLabel.GpuType,
    field: (node: INodeTable): string => node.gpuInfo?.gpuType || "-",
    sortable: false,
    align: "left",
    hideFilter: true,
  },
  totalGpus: {
    name: nodeColumnName.gpuCount,
    label: ENodeColumnLabel.GpuCount,
    field: (node: INodeTable): string => tableFormatUtil.getFormattedNumberOrDefault(node.gpuInfo?.gpuCount),
    sortable: false,
    align: "left",
    hideFilter: true,
  },
  nvLinkDomainUid: {
    name: nodeColumnName.nvLinkDomainUid,
    label: ENodeColumnLabel.NvLinkDomainUid,
    field: (node: INodeTable): string => node.nvLinkDomainUid || "-",
    sortable: true,
    align: "left",
    filter: {
      type: EColumnFilterType.FreeText,
    },
  },
  nvLinkCliqueId: {
    name: nodeColumnName.nvLinkCliqueId,
    label: ENodeColumnLabel.NvLinkCliqueId,
    field: (node: INodeTable): string => node.nvLinkCliqueId || "-",
    sortable: true,
    align: "left",
    filter: {
      type: EColumnFilterType.FreeText,
    },
  },
  cluster: {
    name: nodeColumnName.cluster,
    label: ENodeColumnLabel.Cluster,
    field: (row: Node): string => row.clusterUuid,
    sortable: false,
    align: "left",
    customCell: ECustomCell.CLUSTER_ID_TO_NAME_COL,
    hideFilter: true,
  },
  /*** nodes telemetry columns ***/
  totalGpuMemoryBytes: {
    name: nodeColumnName.totalGpuMemoryBytes,
    label: ENodeColumnLabel.TotalGpuMemoryBytes,
    field: (node: INodeTable): string => _formatMemoryValue(node.additionalFields?.totalGpuMemoryBytes),
    sortable: false,
    align: "left",
    hideFilter: true,
  },
  allocatedGpus: {
    name: nodeColumnName.allocatedGpus,
    label: ENodeColumnLabel.AllocatedGpus,
    field: (node: INodeTable): string =>
      tableFormatUtil.getFormattedNumberOrDefault(node.additionalFields?.allocatedGpus),
    sortable: false,
    align: "left",
    hideFilter: true,
  },
  totalCpuCores: {
    name: nodeColumnName.totalCpuCores,
    label: ENodeColumnLabel.TotalCpuCores,
    field: (node: INodeTable): string =>
      tableFormatUtil.getFormattedNumberOrDefault(node.additionalFields?.totalCpuCores),
    sortable: false,
    align: "left",
    hideFilter: true,
  },
  totalCpuMemoryBytes: {
    name: nodeColumnName.totalCpuMemoryBytes,
    label: ENodeColumnLabel.TotalCpuMemoryBytes,
    field: (node: INodeTable): string => _formatMemoryValue(node.additionalFields?.totalCpuMemoryBytes),
    sortable: false,
    align: "left",
    hideFilter: true,
  },
  allocatedCpuCores: {
    name: nodeColumnName.allocatedCpuCores,
    label: ENodeColumnLabel.AllocatedCpuCores,
    field: (node: INodeTable): string =>
      tableFormatUtil.getFormattedNumberOrDefault(node.additionalFields?.allocatedCpuCores),
    sortable: false,
    align: "left",
    hideFilter: true,
  },
  allocatedCpuMemoryBytes: {
    name: nodeColumnName.allocatedCpuMemoryBytes,
    label: ENodeColumnLabel.AllocatedCpuMemoryBytes,
    field: (node: INodeTable): string => _formatMemoryValue(node.additionalFields?.allocatedCpuMemoryBytes),
    sortable: false,
    align: "left",
    hideFilter: true,
  },
  readyTotalGPU: {
    name: nodeColumnName.readyTotalGPU,
    label: ENodeColumnLabel.ReadyTotalGPU,
    field: (node: INodeTable): string =>
      formatReadyTotalGpu(node.additionalFields?.readyGpus, node.additionalFields?.totalGpus),
    sortable: false,
    align: "left",
    hideFilter: true,
  },
  freeGpu: {
    name: nodeColumnName.freeGpu,
    label: ENodeColumnLabel.FreeGpu,
    field: (node: INodeTable): string | number => node.additionalFields?.freeGpus || "-",
    sortable: false,
    align: "left",
    hideFilter: true,
  },
  allocatedGpuMemory: {
    name: nodeColumnName.allocatedGpuMemory,
    label: ENodeColumnLabel.AllocatedGpuMemory,
    field: (): string => "-",
    sortable: false,
    align: "left",
    hideFilter: true,
  },
  pods: {
    name: nodeColumnName.pods,
    label: ENodeColumnLabel.Pods,
    field: () => "View",
    sortable: false,
    align: "left",
    customCell: ECustomCell.LINK_COL,
    customCellEvent: { emitName: "pods-clicked" },
    hideFilter: true,
    exportFormat: () => "-",
  },
};

export const nodeModalColumns: ITableColumn[] = [
  { ...nodeV2IndexColumns.name },
  { ...nodeV2IndexColumns.gpuType },
  { ...nodeV2IndexColumns.totalGpus },
  { ...nodeV2IndexColumns.totalGpuMemoryBytes },
  { ...nodeV2IndexColumns.allocatedGpus },
  { ...nodeV2IndexColumns.totalCpuCores },
  { ...nodeV2IndexColumns.totalCpuMemoryBytes },
  { ...nodeV2IndexColumns.allocatedCpuCores },
  { ...nodeV2IndexColumns.allocatedCpuMemoryBytes },
];

export const nodeIndexColumns: ITableColumn[] = [
  { ...nodeV2IndexColumns.name, display: true, mandatory: true },
  { ...nodeV2IndexColumns.status, display: true },
  { ...nodeV2IndexColumns.nodePool, display: true },
  { ...nodeV2IndexColumns.cluster, display: true },
  { ...nodeV2IndexColumns.nvLinkDomainUid, display: true },
  { ...nodeV2IndexColumns.nvLinkCliqueId, display: true },
  { ...nodeV2IndexColumns.readyTotalGPU, display: true },
  { ...nodeV2IndexColumns.gpuType, display: true },
  { ...nodeV2IndexColumns.freeGpu, display: true },
  { ...nodeV2IndexColumns.allocatedGpus, display: false },
  { ...nodeV2IndexColumns.allocatedCpuCores, display: false },
  { ...nodeV2IndexColumns.allocatedCpuMemoryBytes, display: false },
  { ...nodeV2IndexColumns.pods, display: false },
];

export const nodeDependentColumns = {
  nodePool: new Set([nodeColumnName.nodePool]), //only shown when there more than one node pool
  cluster: new Set([nodeColumnName.cluster]), //only shown when there more than one cluster
  mnnvl: new Set([
    //only shown when cluster version is 2.21 or higher
    nodeColumnName.nvLinkDomainUid,
    nodeColumnName.nvLinkCliqueId,
  ]),
};
